import { useMemo } from "react";

import {
  Avatar,
  Box,
  CheckIcon,
  FormField,
  MultiSelect,
  Row,
  Select,
  Text,
  Tooltip,
  UserIcon,
  TextWithTooltip,
} from "@hightouch/htui";
import pluralize from "pluralize";
import { Controller } from "react-hook-form";

import { useBoard } from "src/pages/projects/board-context";
import { PROJECTS_TERMS as TERMS } from "src/pages/projects/constants";
import { useGroups, useWorkspaceMembers } from "src/pages/projects/hooks";

import { GroupAvatar } from "./group-avatar";
import { type Appearance, PropertyPicker } from "./property-picker";

/** Someone in a stack; a user group shows as a group avatar. */
type Person = { id: string | number; name: string; isGroup?: boolean };

type AvatarStackProps = {
  people: Person[];
  /** How many avatars show before the rest collapse into a count. */
  max?: number;
};

/** Overlapping avatars, named in one tooltip. */
export const AvatarStack = ({ people, max = 4 }: AvatarStackProps) => {
  const shown = people.slice(0, max);
  const hidden = people.length - shown.length;
  return (
    <Tooltip
      message={people.map((person) => person.name).join(", ")}
      isDisabled={people.length === 0}
    >
      <Row align="center" flexShrink={0}>
        {shown.map((person, index) => (
          <Box
            key={person.id}
            ml={index === 0 ? 0 : -1.5}
            borderRadius="full"
            bg="white"
            p="1px"
            display="flex"
          >
            {person.isGroup ? (
              <GroupAvatar />
            ) : (
              <Avatar name={person.name} size="sm" hideTooltip />
            )}
          </Box>
        ))}
        {hidden > 0 && (
          <Text size="sm" color="text.tertiary" ml={1}>
            +{hidden}
          </Text>
        )}
      </Row>
    </Tooltip>
  );
};

/** A task's or project's user row, which carries the user or group as the server last saw them. */
type PickedPerson = {
  user_id: string | number | null;
  user: { name: string } | null;
  user_group_id?: string | number | null;
  user_group?: { name: string } | null;
};

/** A workspace member or, where groups are allowed, a group, keyed apart since their ids can match. */
type PersonOption = {
  key: string;
  id: string;
  name: string;
  description: string;
  isGroup: boolean;
};

const userKey = (id: string) => `user:${id}`;
const groupKey = (id: string) => `group:${id}`;

/** The workspace's members and, with groups, its groups first, as picker options. */
function usePersonOptions(withGroups: boolean) {
  const { members, isLoading: isLoadingMembers } = useWorkspaceMembers();
  const { groups, isLoading: isLoadingGroups } = useGroups({
    enabled: withGroups,
  });
  const options = useMemo<PersonOption[]>(
    () => [
      ...(withGroups ? groups : []).map((group) => ({
        key: groupKey(group.id),
        id: group.id,
        name: group.name,
        description: `${TERMS.group.Singular}, ${pluralize("member", group.members.length, true)}`,
        isGroup: true,
      })),
      ...members.map((member) => ({
        key: userKey(member.id),
        id: member.id,
        name: member.name,
        description: member.email,
        isGroup: false,
      })),
    ],
    [withGroups, groups, members],
  );
  return {
    options,
    isLoading: isLoadingMembers || (withGroups && isLoadingGroups),
  };
}

type PeoplePickerProps = {
  /** What the people are to the task or project, such as "Assignees". */
  label: string;
  people: PickedPerson[];
  onChange: (userIds: string[]) => void;
  /** Lets groups be picked too, and sets the picked groups' ids. */
  onGroupsChange?: (userGroupIds: string[]) => void;
  /** Holds one person: a pick replaces whoever is there. */
  isSingle?: boolean;
  emptyLabel: string;
  appearance?: Appearance;
  /** Shows only the avatars, for dense rows. */
  avatarsOnly?: boolean;
};

/**
 * Picks workspace members, and groups where allowed. Each pick toggles one
 * person, so the list reads as a checklist of who's on it.
 */
export const PeoplePicker = ({
  label,
  people: picked,
  onChange,
  onGroupsChange,
  isSingle = false,
  emptyLabel,
  appearance = "pill",
  avatarsOnly = false,
}: PeoplePickerProps) => {
  const { memberById } = useBoard();
  const { options: allOptions, isLoading } = usePersonOptions(
    Boolean(onGroupsChange),
  );
  const userIds = picked.flatMap((person) =>
    person.user_id == null ? [] : [String(person.user_id)],
  );
  const groupIds = picked.flatMap((person) =>
    person.user_group_id == null ? [] : [String(person.user_group_id)],
  );
  const selected = new Set([
    ...userIds.map(userKey),
    ...groupIds.map(groupKey),
  ]);
  // The row's own name stands in while members load, and after they leave the workspace.
  const people = picked.flatMap((person) => {
    if (person.user_group_id != null) {
      const name = person.user_group?.name;
      return name
        ? [{ id: groupKey(String(person.user_group_id)), name, isGroup: true }]
        : [];
    }
    const id = String(person.user_id);
    const name = memberById.get(id)?.name ?? person.user?.name;
    return name ? [{ id: userKey(id), name }] : [];
  });
  // Someone who left the workspace has no option of their own, so their row brings one to remove them by.
  const optionKeys = new Set(allOptions.map((option) => option.key));
  const formerMembers = picked.flatMap((person): PersonOption[] => {
    if (isLoading || person.user_id == null) return [];
    const id = String(person.user_id);
    return optionKeys.has(userKey(id))
      ? []
      : [
          {
            key: userKey(id),
            id,
            name: person.user?.name ?? "Unknown user",
            description: "Not in this workspace",
            isGroup: false,
          },
        ];
  });
  // Chosen people first, so removing one is as quick as adding.
  const options = [...allOptions, ...formerMembers].sort(
    (a, b) => Number(selected.has(b.key)) - Number(selected.has(a.key)),
  );
  const summary =
    people.length === 0
      ? emptyLabel
      : people.length === 1
        ? (people[0]?.name ?? emptyLabel)
        : `${people.length} ${label.toLowerCase()}`;

  return (
    <PropertyPicker<PersonOption, string>
      aria-label={`${label}: ${summary}. Change ${label.toLowerCase()}`}
      appearance={appearance}
      options={options}
      value={undefined}
      optionValue={(option) => option.key}
      optionLabel={(option) => option.name}
      optionDescription={(option) => option.description}
      optionAccessories={(option) =>
        selected.has(option.key)
          ? { rightAccessory: { type: "icon", icon: CheckIcon } }
          : undefined
      }
      isLoading={isLoading}
      placeholder={
        isSingle
          ? `Choose ${label.toLowerCase()}…`
          : `Add or remove ${label.toLowerCase()}…`
      }
      onChange={(key) => {
        const option = options.find((candidate) => candidate.key === key);
        if (!option) return;
        const isPicked = selected.has(option.key);
        const ids = option.isGroup ? groupIds : userIds;
        const next = isPicked
          ? ids.filter((id) => id !== option.id)
          : isSingle
            ? [option.id]
            : [...ids, option.id];
        if (option.isGroup) onGroupsChange?.(next);
        else onChange(next);
      }}
    >
      {people.length > 0 ? (
        <AvatarStack people={people} max={avatarsOnly ? 3 : 4} />
      ) : (
        <Box as={UserIcon} boxSize={4} color="text.tertiary" />
      )}
      {/* Several people read as their avatars, named in the stack's tooltip. */}
      {!avatarsOnly && people.length <= 1 && (
        <TextWithTooltip
          color={people.length > 0 ? undefined : "text.tertiary"}
        >
          {summary}
        </TextWithTooltip>
      )}
    </PropertyPicker>
  );
};

type MemberFieldProps = {
  /** The form field that holds the ids. */
  name: string;
  label: string;
  emptyLabel: string;
};

/** Picks one workspace member in a form, as a user id, or "" for no one. */
export const MemberSelect = ({ name, label, emptyLabel }: MemberFieldProps) => {
  const { members, isLoading } = useWorkspaceMembers();

  return (
    <Controller
      name={name}
      render={({ field }) => (
        <FormField label={label} isOptional>
          <Select
            width="100%"
            isClearable
            isLoading={isLoading}
            options={members}
            optionValue={(member) => member.id}
            optionLabel={(member) => member.name}
            optionDescription={(member) => member.email}
            placeholder={emptyLabel}
            value={field.value || undefined}
            onChange={(userId) => field.onChange(userId ?? "")}
          />
        </FormField>
      )}
    />
  );
};

/**
 * Picks workspace members in a form, as user ids. With groups, it holds
 * `user:<id>` and `group:<id>` keys instead, which `splitPersonKeys` reads.
 */
export const MemberMultiSelect = ({
  name,
  label,
  emptyLabel,
  withGroups = false,
}: MemberFieldProps & { withGroups?: boolean }) => {
  const { options, isLoading } = usePersonOptions(withGroups);

  return (
    <Controller
      name={name}
      render={({ field }) => (
        <FormField label={label} isOptional>
          <MultiSelect
            width="100%"
            isClearable
            isLoading={isLoading}
            options={options}
            optionValue={(option) => (withGroups ? option.key : option.id)}
            optionLabel={(option) => option.name}
            optionDescription={(option) => option.description}
            placeholder={emptyLabel}
            value={field.value}
            onChange={(keys) => field.onChange(keys ?? [])}
          />
        </FormField>
      )}
    />
  );
};
